import { FC, useEffect, useMemo, useRef, useState } from "react";
import moment, { Moment } from "moment";
import { Item, ItemProps } from ".";
import AppDropdownContent from "components/AppDropdownContent";

interface DatePickerProps extends ItemProps {
    value?: string | Date | Moment;
    onChange?: (value?: string) => void;
    placeholder?: string;
    format?: string;
}

const DatePicker: FC<DatePickerProps> = ({
    value,
    onChange,
    col,
    error,
    label,
    placeholder,
    required,
    format = "DD/MM/YYYY HH:mm",
    itemClassName
}) => {
    const internalValue = useMemo(() => {
        return value ? moment(value, format, true).format(format) : "";
    }, [value, format])
    const [showCalendar, setShowCalendar] = useState(false);
    const calendarRef = useRef<HTMLDivElement>(null);

    const inputClassName = useMemo(() => {
        let className = `@app-form-date-picker w-full border px-4 py-2 rounded focus:outline-none focus:border-blue-500 bg-white cursor-pointer pr-8 `;
        if (Boolean(error)) {
            className += " border-red-500 focus:border-red-500 focus:ring-red-500";
        }
        return className;
    }, [error]);
    const clearDate = () => {
        onChange && onChange(undefined);
    };

    return (
        <Item col={col} label={label} required={required} error={error} itemClassName={itemClassName}>
            <div className="relative group/date-picker" ref={calendarRef}>
                <input
                    value={internalValue}
                    readOnly
                    className={inputClassName}
                    placeholder={placeholder || format}
                    onClick={() => setShowCalendar(true)}
                />
                {/* Nút clear */}
                {internalValue && (
                    <span
                        className="absolute w-6 h-6 mr-2 right-8 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 opacity-0 group-hover/date-picker:opacity-100 transition-opacity duration-200"
                        onClick={clearDate}
                    >
                        ✕
                    </span>
                )}
                {/* Icon calendar */}
                <span
                    className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
                    onClick={() => setShowCalendar((prev) => !prev)}
                >
                    📅
                </span>
                <AppDropdownContent open={showCalendar} onClose={() => setShowCalendar(false)} triggerRef={calendarRef} className="w-80" fullWidth={false}>
                    <Calendar value={value} onChange={onChange} format={format} onClose={() => setShowCalendar(false)} />
                </AppDropdownContent>
            </div>
        </Item>
    );
};


interface CalendarProps {
    value?: string | Date | Moment;
    onChange?: (value?: string) => void;
    format?: string;
    onClose?: () => void;
}
export const Calendar: FC<CalendarProps> = ({
    value,
    onChange,
    format = "DD/MM/YYYY HH:mm",
    onClose
}) => {
    const [internalValue, setInternalValue] = useState<string>(
        value ? moment(value, format, true).format(format) : ""
    );

    const [viewDate, setViewDate] = useState<Moment>(
        moment(value, format, true).isValid()
            ? moment(value, format, true)
            : moment()
    );
    const [hour, setHour] = useState<number>(
        moment(value).isValid() ? moment(value).hour() : 0
    );
    const [minute, setMinute] = useState<number>(
        moment(value).isValid() ? moment(value).minute() : 0
    );
    const hasTime = useMemo(() => /H|h/.test(format), [format]);

    const selectDate = (day: Moment) => {
        let finalDate = day.clone();
        if (hasTime) {
            finalDate = finalDate.hour(hour).minute(minute);
        }
        const formatted = finalDate.format(format);
        setInternalValue(formatted);
        onChange && onChange(formatted);
        setViewDate(finalDate);
        onClose && onClose();
    };

    const renderCalendar = () => {
        const startOfMonth = viewDate.clone().startOf("month").startOf("week");
        const endOfMonth = viewDate.clone().endOf("month").endOf("week");
        const days: Moment[] = [];
        let day = startOfMonth.clone();

        while (day.isBefore(endOfMonth)) {
            days.push(day.clone());
            day.add(1, "day");
        }

        const months = moment.months();
        const years = Array.from(
            { length: 101 },
            (_, i) => moment().year() - 50 + i
        );

        return (
            <div className="bg-white border rounded shadow-lg p-3 w-80">
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                    <button
                        type="button"
                        className="px-2 py-1 hover:bg-gray-200 rounded"
                        onClick={() =>
                            setViewDate(viewDate.clone().subtract(1, "month"))
                        }
                    >
                        ←
                    </button>
                    <div className="flex gap-2">
                        <select
                            value={viewDate.month()}
                            onChange={(e) =>
                                setViewDate(
                                    viewDate.clone().month(parseInt(e.target.value))
                                )
                            }
                            className="border rounded px-1"
                        >
                            {months.map((m, i) => (
                                <option value={i} key={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                        <select
                            value={viewDate.year()}
                            onChange={(e) =>
                                setViewDate(
                                    viewDate.clone().year(parseInt(e.target.value))
                                )
                            }
                            className="border rounded px-1"
                        >
                            {years.map((y) => (
                                <option value={y} key={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        className="px-2 py-1 hover:bg-gray-200 rounded"
                        onClick={() =>
                            setViewDate(viewDate.clone().add(1, "month"))
                        }
                    >
                        →
                    </button>
                </div>

                {/* Weekdays */}
                <div className="grid grid-cols-7 gap-1 text-center text-sm font-medium mb-1">
                    {moment.weekdaysShort().map((d) => (
                        <div key={d}>{d}</div>
                    ))}
                </div>

                {/* Days */}
                <div className="grid grid-cols-7 gap-1 text-center">
                    {days.map((d, idx) => {
                        const isCurrentMonth = d.month() === viewDate.month();
                        const isSelected =
                            moment(internalValue, format, true).isValid() &&
                            d.isSame(
                                moment(internalValue, format, true),
                                "day"
                            );

                        const today = moment();
                        const isToday = d.isSame(today, "day");

                        return (
                            <button
                                type="button"
                                key={idx}
                                onClick={() => selectDate(d)}
                                className={`p-1 rounded ${isToday && !isSelected ?
                                    "bg-blue-100"
                                    : isSelected
                                        ? "bg-blue-500 text-white"
                                        : isCurrentMonth
                                            ? "hover:bg-gray-200"
                                            : "text-gray-400"
                                    }`}
                            >
                                {d.date()}
                            </button>
                        );
                    })}
                </div>

                {/* Time Picker (only if format has time) */}
                {hasTime && (
                    <div className="flex items-center justify-center gap-2 mt-3">
                        <b>Thời gian:</b>
                        <select
                            value={hour}
                            onChange={(e) => setHour(parseInt(e.target.value))}
                            className="border rounded px-1 py-0.5"
                        >
                            {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>
                                    {i.toString().padStart(2, "0")}
                                </option>
                            ))}
                        </select>
                        :
                        <select
                            value={minute}
                            onChange={(e) => setMinute(parseInt(e.target.value))}
                            className="border rounded px-1 py-0.5"
                        >
                            {Array.from({ length: 60 }, (_, i) => (
                                <option key={i} value={i}>
                                    {i.toString().padStart(2, "0")}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        );
    };

    return renderCalendar();
};
export default DatePicker;